import Fingerprint2 from 'fingerprintjs2';
import SimpleCrypto from 'simple-crypto-js';

const simpleCrypto = new SimpleCrypto('');

// `webglVendorAndRenderer` and `plugins` are excluded because they can drift
// between encrypt and decrypt on iOS 26+ and Android 16, which silently
// corrupts the AES key derived from this hash.
const fingerprintOptions: Fingerprint2.Options = {
  excludes: {
    userAgent: true,
    language: true,
    colorDepth: true,
    deviceMemory: false,
    pixelRatio: true,
    hardwareConcurrency: false,
    screenResolution: false,
    availableScreenResolution: true,
    timezoneOffset: true,
    timezone: true,
    sessionStorage: false,
    localStorage: true,
    indexedDb: true,
    addBehavior: false,
    openDatabase: true,
    cpuClass: false,
    platform: false,
    doNotTrack: false,
    plugins: true,
    canvas: true,
    webgl: true,
    webglVendorAndRenderer: true,
    adBlock: true,
    hasLiedLanguages: true,
    hasLiedResolution: true,
    hasLiedOs: true,
    hasLiedBrowser: true,
    touchSupport: false,
    fonts: true,
    fontsFlash: true,
    audio: true,
    enumerateDevices: true,
  },
};

// Pre-fix fingerprint inputs, kept verbatim so ciphertext written by the
// previous app version still decrypts on a device whose fingerprint hasn't
// drifted. Read-only fallback path — never used to encrypt new data.
const legacyFingerprintOptions: Fingerprint2.Options = {
  excludes: {
    userAgent: true,
    language: true,
    colorDepth: true,
    deviceMemory: false,
    pixelRatio: true,
    hardwareConcurrency: false,
    screenResolution: false,
    availableScreenResolution: true,
    timezoneOffset: true,
    timezone: true,
    sessionStorage: false,
    localStorage: true,
    indexedDb: true,
    addBehavior: false,
    openDatabase: true,
    cpuClass: false,
    platform: false,
    doNotTrack: false,
    plugins: false,
    canvas: true,
    webgl: true,
    webglVendorAndRenderer: false,
    adBlock: true,
    hasLiedLanguages: true,
    hasLiedResolution: true,
    hasLiedOs: true,
    hasLiedBrowser: true,
    touchSupport: false,
    fonts: true,
    fontsFlash: true,
    audio: true,
    enumerateDevices: true,
  },
};

const computeFingerprintKey = (
  options: Fingerprint2.Options
): Promise<string | Error> => {
  return Fingerprint2.getPromise(options)
    .then(components => {
      const values = components.map(component => component.value);
      return Fingerprint2.x64hash128(values.join(''), 31);
    })
    .catch((reason: Error) => reason);
};

const getSecretKey = (): Promise<string | Error> =>
  computeFingerprintKey(fingerprintOptions);

const getLegacySecretKey = (): Promise<string | Error> =>
  computeFingerprintKey(legacyFingerprintOptions);

const tryDecrypt = (data: string, key: string | Error): string | undefined => {
  try {
    simpleCrypto.setSecret(key as string);
    const result = simpleCrypto.decrypt(data).toString();
    return result || undefined;
  } catch (e) {
    return undefined;
  }
};

export const encrypt = async (data: string): Promise<string | undefined> => {
  try {
    const secretKey = await getSecretKey();
    simpleCrypto.setSecret(secretKey);
    const encryptedData = simpleCrypto.encrypt(data);
    return encryptedData;
  } catch (e) {
    return undefined;
  }
};

export const decrypt = async (data: string): Promise<string | undefined> => {
  if (!data) return undefined;
  const newKey = await getSecretKey();
  let result = tryDecrypt(data, newKey);
  if (result === undefined) {
    const legacyKey = await getLegacySecretKey();
    result = tryDecrypt(data, legacyKey);
  }
  return result;
};

export const decryptBatch = async (
  data: (string | undefined)[]
): Promise<(string | undefined)[] | undefined> => {
  const newKey = await getSecretKey();
  let legacyKeyPromise: Promise<string | Error> | undefined;
  const getLegacyKey = () => {
    if (!legacyKeyPromise) legacyKeyPromise = getLegacySecretKey();
    return legacyKeyPromise;
  };

  let hasInput = false;
  let anyDecoded = false;

  const decoded: (string | undefined)[] = [];
  for (const value of data) {
    if (!value) {
      decoded.push(value);
      continue;
    }
    hasInput = true;
    let item = tryDecrypt(value, newKey);
    if (item === undefined) {
      item = tryDecrypt(value, await getLegacyKey());
    }
    // decryptBatch is only used for signature payloads; anything that doesn't
    // start with a data-image URL is a wrong-key decode that happened to
    // produce valid UTF-8. Treating it as undefined stops malformed blobs from
    // ever reaching the upload.
    if (item !== undefined && !item.startsWith('data:image/')) {
      item = undefined;
    }
    if (item !== undefined) anyDecoded = true;
    decoded.push(item);
  }

  // Preserve the original caller contract: a fully-failed batch returns
  // undefined so the existing "Decryption failed" + reset flow fires.
  if (hasInput && !anyDecoded) return undefined;
  return decoded;
};

export const cipherOcuco = (
  value: string,
  encrypt: boolean = false
): string => {
  /* let charCode = 0;
  let charChar = '';
  let result = '';
  for (let i = 0; i < value.length; i++) {
    charCode = value[i].charCodeAt(0);
    if (charCode >= 65 && charCode <= 90) {
      if (charCode + i < 65) {
        charCode += i + 26;
      } else {
        charCode += i;
      }
    } else if (charCode >= 97 && charCode <= 122) {
      if (charCode + i < 97) {
        charCode += i + 26;
      } else {
        charCode += i;
      }
    } else {
      charCode += i;
    }

    charChar = String.fromCharCode(charCode);
    result += charChar;
  }

  return result;*/

  return value;
};

export const decodeURL = (url: string) => {
  let result = '';
  if (url.length > 0) {
    let i = 0;
    while (i <= url.length) {
      if (url[i] === '%') {
        const hexaDecimal = url[i + 1] + url[i + 2];
        result += String.fromCharCode(parseInt(hexaDecimal, 16));
        i += 2;
      } else {
        result += url[i];
      }
      i++;
    }
  }

  return result;
};
